import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { rtkQueryErrorLogger } from "./middleware/rtkQueryErrorLogger";
import { authApiSlice } from "./services/authApiSlice";
import authReducer from "./services/authSlice";
import { cemeterySearchApi } from "./services/cemeterySearchSlice";
import { cemeterySlice } from "./services/cemeterySlice";
import { companySlice } from "./services/companySlice";
import { customerSlice } from "./services/customerSlice";
import impersonationReducer, {
  impersonationApi,
} from "./services/impersonationSlice";
import { orderSlice } from "./services/orderSlice";
import { tenantSlice } from "./services/tenantSlice";
import { uploadApiSlice } from "./services/uploadSlice";

const appReducer = combineReducers({
  auth: authReducer,
  impersonation: impersonationReducer,
  [orderSlice.reducerPath]: orderSlice.reducer,
  [cemeterySlice.reducerPath]: cemeterySlice.reducer,
  [cemeterySearchApi.reducerPath]: cemeterySearchApi.reducer,
  [customerSlice.reducerPath]: customerSlice.reducer,
  [companySlice.reducerPath]: companySlice.reducer,
  [uploadApiSlice.reducerPath]: uploadApiSlice.reducer,
  [impersonationApi.reducerPath]: impersonationApi.reducer,
  [tenantSlice.reducerPath]: tenantSlice.reducer,
  [authApiSlice.reducerPath]: authApiSlice.reducer,
});

const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: { type: string },
) => {
  if (action.type === "auth/logout") {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // optimize serialization check for performance
      serializableCheck: {
        warnAfter: 128,
        ignoredActions: [
          // ignore rtk query actions for performance
          "orderSlice/executeQuery/fulfilled",
          "customerSlice/executeQuery/fulfilled",
          "cemeterySlice/executeQuery/fulfilled",
          "tenantSlice/executeQuery/fulfilled",
        ],
      },
    })
      .concat(companySlice.middleware)
      .concat(customerSlice.middleware)
      .concat(orderSlice.middleware)
      .concat(cemeterySlice.middleware)
      .concat(cemeterySearchApi.middleware)
      .concat(uploadApiSlice.middleware)
      .concat(impersonationApi.middleware)
      .concat(tenantSlice.middleware)
      .concat(authApiSlice.middleware)
      .concat(rtkQueryErrorLogger),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export default store;
